import { AdCreative } from '@core/models/platforms/adcreative';
import { UpdateImageGenerationTaskPayload } from '@pages/create-ad/state/adc-image-generation/actions';
import { GUID } from '@core/models';
import { notNil } from '@core/utils';

/**
 * Maps unfinished tasks to the format required by the UpdateImageGenerationTaskPayload.
 * @param tasks - The unfinished tasks to map.
 */
export function mapUnfinishedTasks(
  tasks: AdCreative.Backend.ImageGenerationTask
): Array<UpdateImageGenerationTaskPayload> {
  return Object.entries(tasks).map(([id, renderState]) => mapUnfinishedTask(id as GUID, renderState));
}

/**
 * Maps finished tasks to the format required by the UpdateImageGenerationTaskPayload.
 * @param tasks - The finished tasks to map.
 */
export function mapFinishedTasks(
  tasks: Array<AdCreative.Backend.ImageGenerationTaskScore>
): Array<UpdateImageGenerationTaskPayload> {
  return tasks.map((task) => mapFinishedTask(task));
}

/**
 * Maps tasks to the format required by the UpdateImageGenerationTaskPayload.
 * @param tasks - The tasks to map, which can be either unfinished or finished tasks.
 */
export function mapTasks(
  tasks: Array<AdCreative.Backend.ImageGenerationTaskScore> | AdCreative.Backend.ImageGenerationTask
): Array<UpdateImageGenerationTaskPayload> {
  return Array.isArray(tasks) ? mapFinishedTasks(tasks) : mapUnfinishedTasks(tasks);
}

/**
 * Maps an unfinished task to the format required by the UpdateImageGenerationTaskPayload.
 * @param id - The ID of the task.
 * @param renderState - The render state of the task.
 */
export function mapUnfinishedTask(
  id: GUID,
  renderState: AdCreative.Backend.RenderState
): UpdateImageGenerationTaskPayload {
  return {
    id,
    status: renderState,
    score: 0
  };
}

/**
 * Maps a finished task to the format required by the UpdateImageGenerationTaskPayload.
 * @param task - The finished task to map.
 */
export function mapFinishedTask(task: AdCreative.Backend.ImageGenerationTaskScore): UpdateImageGenerationTaskPayload {
  return {
    id: task.taskId,
    status: task.renderState,
    score: task.score
  };
}

/**
 * Maps a task to the format required by the UpdateImageGenerationTaskPayload.
 * @param task - The task to map, which can be either unfinished or finished.
 */
export function mapTask(
  task: AdCreative.Backend.ImageGenerationTaskScore | AdCreative.Backend.ImageGenerationTask
): UpdateImageGenerationTaskPayload {
  if (isTaskFinished(task)) {
    return mapFinishedTask(task);
  }
  const [id, renderState] = Object.entries(task)[0] as [GUID, AdCreative.Backend.RenderState];
  return mapUnfinishedTask(id, renderState);
}

/**
 * Checks if a task is finished.
 * @param task - The task to check.
 * @returns True if the task is finished, false otherwise.
 */
export function isTaskFinished(
  task: AdCreative.Backend.ImageGenerationTaskScore | AdCreative.Backend.ImageGenerationTask
): task is AdCreative.Backend.ImageGenerationTaskScore {
  const score = (task as AdCreative.Backend.ImageGenerationTaskScore).score;
  return notNil(score) && score !== 0;
}
